import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import {
	isSupplierPayingCustomerAndAdmin,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { UserName } from '../name_component/user/UserNameComponent';
import { ProtectedImageThumbnails } from '../protected_image_thumbnails/ProtectedImageThumbnails';
import { Button, Card, Divider, Popover, Timeline } from 'antd';
import moment from 'moment';
import { isInternalTech, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import {
	getSupplierWorkOrderServiceCalls,
	isDifferentAssignedSupplierBasedOnView,
	isSubContractedWorkOrder,
} from '../../utils/WorkOrderUtils';
import { CopyOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';

const SupplierServiceCallDetails: FC<any> = ({
	currentUser,
	workOrder,
	handleManualCheckOut,
	showApproveModal,
	showRejectModal,
	handleReschedule,
	handleManualCheckIn,
	isReadOnly,
	isClientView,
	copyServiceCall,
	handleTechEnRoute,
}): React.ReactElement => {
	const userType = useMemo(() => currentUser.userType, [currentUser.userType]);

	const isPrivateSupplier = useMemo(() => isInternalTech(currentUser), [currentUser]);

	const isTechEnRouteEnabled = posthog.isFeatureEnabled('tech-enroute-status');

	const differentAssignedSupplier = useMemo(
		() => isDifferentAssignedSupplierBasedOnView(workOrder, currentUser, isClientView),
		[currentUser, isClientView, workOrder]
	);

	const getServiceCallDetails = useCallback(
		(serviceCall) => {
			const scheduledAt = serviceCall.serviceScheduledAt
				? [
						<div>
							<div style={{ fontSize: 16 }}>
								Scheduled to arrive by{' '}
								<DateTimeHover
									timestamp={nullSafeGet('serviceScheduledAt', serviceCall)}
									dateFormat={'h:mm a z on MMM D'}
									showDate
									parseZone={false}
									timeZone={nullSafeGet('location.timeZoneId', workOrder)}
								/>
								.
							</div>
							{!serviceCall.checkOutStatus &&
							!isReadOnly &&
							(workOrder.status === 'TechScheduled' ||
								workOrder.status === 'TechRescheduled' ||
								workOrder.status === 'TechEnRoute') ? (
								<div className="mt-4 flex flex-row gap-x-4">
									<Button type="primary" key="manualCheckIn" onClick={handleManualCheckIn}>
										Remote Check In
									</Button>
									{workOrder.status === 'TechEnRoute' || !isTechEnRouteEnabled ? null : (
										<Button key="techEnRoute" onClick={handleTechEnRoute}>
											En route
										</Button>
									)}
									<Button key="reschedule" onClick={handleReschedule}>
										Reschedule
									</Button>
								</div>
							) : null}
						</div>,
				  ]
				: [];

			const startedWorkAt = serviceCall.checkInTime
				? [
						<div style={{ fontSize: 16 }}>
							<div>
								Started work at{' '}
								<DateTimeHover
									timestamp={nullSafeGet('checkInTime', serviceCall)}
									dateFormat={'h:mm a z on MMM D'}
									showDate
									timeZone={nullSafeGet('location.timeZoneId', workOrder)}
								/>
								.
								{serviceCall.checkInGeoLocation ? null : nullSafeGet(
										`checkInByContact`,
										serviceCall
								  ) ? (
									<span style={{ color: 'rgba(0,0,0,0.45)' }}>
										{' '}
										(Checked in remotely by{' '}
										<UserName
											mode={'inline'}
											contact={nullSafeGet(`checkInByContact`, serviceCall)}
										/>
										)
									</span>
								) : null}
							</div>
							{serviceCall.checkInNotes && serviceCall.checkInNotes.length > 0 ? (
								<div className="serviceCallNotesBlurb">{serviceCall.checkInNotes}</div>
							) : null}
							{serviceCall.checkInImages && serviceCall.checkInImages.length > 0 ? (
								<ProtectedImageThumbnails
									style={{ marginTop: 16 }}
									imageWidth={64}
									imageHeight={64}
									photoStrings={serviceCall.checkInImages}
									role={userType}
									showDownloadAll
									imageDownloadName={`wo#${workOrder.id}_check_in_`}
								/>
							) : null}
							{!isReadOnly &&
							!serviceCall.checkOutStatus &&
							workOrder.status === 'TechWorkingOnSite' ? (
								<div>
									<Button
										style={{ marginTop: 16 }}
										type="primary"
										key="manualCheckOut"
										onClick={handleManualCheckOut}
									>
										Remote Check Out
									</Button>
								</div>
							) : null}
						</div>,
				  ]
				: [];

			const endedWorkAt = serviceCall.checkOutTime
				? [
						<div style={{ fontSize: 16 }}>
							<div>
								Ended work at{' '}
								<DateTimeHover
									timestamp={nullSafeGet('checkOutTime', serviceCall)}
									dateFormat={'h:mm a z on MMM D'}
									showDate
									timeZone={nullSafeGet('location.timeZoneId', workOrder)}
								/>
								.
								{serviceCall.checkOutGeoLocation ? null : nullSafeGet(
										`checkOutByContact`,
										serviceCall
								  ) ? (
									<span style={{ color: 'rgba(0,0,0,0.45)' }}>
										{' '}
										(Checked out remotely by{' '}
										<UserName
											mode={'inline'}
											contact={nullSafeGet(`checkOutByContact`, serviceCall)}
										/>
										)
									</span>
								) : null}
							</div>
							{serviceCall.checkOutNotes && serviceCall.checkOutNotes.length > 0 ? (
								<div className="serviceCallNotesBlurb">{serviceCall.checkOutNotes}</div>
							) : null}

							{serviceCall.checkOutImages && serviceCall.checkOutImages.length > 0 ? (
								<ProtectedImageThumbnails
									style={{ marginTop: 16 }}
									imageWidth={64}
									imageHeight={64}
									photoStrings={serviceCall.checkOutImages}
									role={userType}
									showDownloadAll
									imageDownloadName={`wo#${workOrder.id}_check_out_`}
								/>
							) : null}
						</div>,
				  ]
				: [];

			const wasOnSiteFor = () => {
				if (!serviceCall || !serviceCall.checkOutTime || !serviceCall.checkInTime) {
					return [];
				}
				//Work completed by third party and is internal technician or a paying supplier admin user
				if (
					(isPrivateSupplier ||
						(isSupplierPayingCustomerAndAdmin(currentUser) &&
							isSubContractedWorkOrder(workOrder))) &&
					differentAssignedSupplier
				) {
					return [
						<div style={{ fontSize: 16 }}>
							{serviceCall.checkInTime && serviceCall.checkOutTime ? (
								<div>
									On site for{' '}
									{moment(serviceCall.checkInTime).from(moment(serviceCall.checkOutTime), true)}
								</div>
							) : null}
							{!isReadOnly &&
							serviceCall.checkOutStatus === 'WaitingForReview' &&
							workOrder.status === 'WaitingForReview'
								? [
										<AccessPermissionChecker
											name={PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED}
										>
											<Button
												style={{ marginTop: 16 }}
												type="primary"
												key="approve"
												onClick={showApproveModal}
											>
												Approve Work
											</Button>
										</AccessPermissionChecker>,
										<AccessPermissionChecker
											name={PERMISSION_NAMES.CAN_REVIEW_WORK_ORDER_COMPLETED}
										>
											<Button
												key="reject"
												style={{ marginLeft: 16, marginTop: 16 }}
												onClick={showRejectModal}
											>
												Reject Work
											</Button>
										</AccessPermissionChecker>,
								  ]
								: null}
						</div>,
					];
				} else if (isPrivateSupplier && !differentAssignedSupplier) {
					//If work was completed by internal technicians
					return [
						<div style={{ fontSize: 16 }}>
							{serviceCall.checkInTime && serviceCall.checkOutTime ? (
								<div>
									On site for{' '}
									{moment(serviceCall.checkInTime).from(moment(serviceCall.checkOutTime), true)}
								</div>
							) : null}
						</div>,
					];
				} else {
					return [
						<div style={{ fontSize: 16 }}>
							{serviceCall.checkInTime && serviceCall.checkOutTime ? (
								<div>
									On site for{' '}
									{moment(serviceCall.checkInTime).from(moment(serviceCall.checkOutTime), true)}
								</div>
							) : null}
						</div>,
					];
				}
			};

			return [...scheduledAt, ...startedWorkAt, ...endedWorkAt, ...wasOnSiteFor()];
		},
		[
			differentAssignedSupplier,
			handleManualCheckIn,
			handleManualCheckOut,
			handleReschedule,
			isPrivateSupplier,
			isReadOnly,
			showApproveModal,
			showRejectModal,
			userType,
			workOrder,
		]
	);

	const ConditionalWrapper = ({ condition, wrapper, children }) =>
		condition ? wrapper(children) : children;

	return (
		<ConditionalWrapper
			condition={isReadOnly}
			wrapper={(children) => <Card title="Service Calls">{children}</Card>}
		>
			{getSupplierWorkOrderServiceCalls(workOrder, currentUser, isClientView).map((sc, scIdx) => [
				<div key={sc.id + '-1'}>
					<div style={{ marginBottom: 24 }}>
						<div className="serviceCallSupplierName" style={{ marginBottom: 8 }}>
							{nullSafeGet('supplierFacility.name', sc)}
							{isClientView ||
							nullSafeGet('originalWorkOrder.displayStatus', workOrder) === 'Completed' ||
							nullSafeGet('originalWorkOrder.displayStatus', workOrder) ===
								'Cancelled' ? null : sc.checkOutTime ? (
								<Popover content="Copy this service call to your client view">
									<Button
										onClick={() => copyServiceCall(sc)}
										icon={<CopyOutlined />}
										style={{ marginLeft: 8 }}
									/>
								</Popover>
							) : null}
						</div>
						{sc.leadTechnicianEmail ? (
							<div
								className="materialCard__primaryTitle"
								style={{
									marginBottom: 0,
									display: 'flex',
									alignItems: 'center',
								}}
							>
								<ContactAvatar
									size="large"
									hidePopover={true}
									style={{ marginRight: 16 }}
									contact={sc.leadTechnicianEmailContact}
									userType={userType}
								/>
								<div>
									<div className="materialCard__title">
										{nullSafeGet('leadTechnicianEmailContact.nameGiven', sc)}{' '}
										{nullSafeGet('leadTechnicianEmailContact.nameFamily', sc)}
									</div>
									<div className="materialCard__subtitle">
										{nullSafeGet('leadTechnicianEmailContact.title', sc)}{' '}
										{nullSafeGet('leadTechnicianEmailContact.department', sc)}
									</div>
								</div>
							</div>
						) : null}
					</div>
					<Timeline>
						{getServiceCallDetails(sc).map((scd, idx) => (
							<Timeline.Item key={idx}>{scd}</Timeline.Item>
						))}
					</Timeline>
				</div>,
				scIdx === nullSafeGetOrElse('associatedServiceCalls', workOrder, []).length - 1 ? null : (
					<Divider key={sc.id + '-2'} />
				),
			])}
		</ConditionalWrapper>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	isClientView: state.clientView.isClientView,
});

export default connect(mapStateToProps)(SupplierServiceCallDetails);
